import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Eye, EyeSlash, Sparkle, Check } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  return typeof detail.msg === "string" ? detail.msg : String(detail);
}

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // login, register, forgot, reset
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccessMsg('');
    setLoading(true);
    try {
      if (mode === 'login') await login(email, password, keepSignedIn);
      else if (mode === 'register') {
        if (!name.trim()) { setError('Name is required'); setLoading(false); return; }
        await register(name, email, password);
      } else if (mode === 'forgot') {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        setSuccessMsg('Check your email for a reset link.');
        if (data.debug_token) setResetToken(data.debug_token); // For demo
        setMode('reset');
      } else if (mode === 'reset') {
        if (!resetToken.trim()) { setError('Enter the reset token'); setLoading(false); return; }
        await axios.post(`${API}/auth/reset-password`, { token: resetToken, password: newPassword });
        setSuccessMsg('Password reset! Sign in with your new password.');
        setMode('login');
        setPassword('');
      }
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-transparent" data-testid="auth-page">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div className="w-full max-w-sm" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
          <Link to="/" className="flex items-center gap-2 mb-10" data-testid="auth-logo">
            <div className="w-7 h-7 rounded-lg bg-lime-400/100 flex items-center justify-center">
              <Sparkle weight="fill" className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Lindey</span>
          </Link>

          <h1 className="text-2xl font-semibold tracking-tight text-white mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            {mode === 'login' ? 'Welcome back' : mode === 'register' ? 'Create your account' : mode === 'forgot' ? 'Forgot password' : 'Reset password'}
          </h1>
          <p className="text-sm text-white/50 mb-7">
            {mode === 'login' ? 'Sign in to continue to Lindey' : mode === 'register' ? 'Start using Lindey for free' : mode === 'forgot' ? 'Enter your email to reset' : 'Enter your new password'}
          </p>

          <form onSubmit={submit} className="space-y-3">
            <AnimatePresence mode="wait">
              {mode === 'register' && (
                <motion.div key="name" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  <Input data-testid="auth-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
                    className="h-11 rounded-lg border-white/10 focus:border-lime-500 focus:ring-lime-500/20 text-sm" />
                </motion.div>
              )}
            </AnimatePresence>

            {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
              <Input data-testid="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
                className="h-11 rounded-lg border-white/10 focus:border-lime-500 focus:ring-lime-500/20 text-sm" required />
            )}

            {(mode === 'login' || mode === 'register') && (
              <div className="relative">
                <Input data-testid="auth-password-input" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password"
                  className="h-11 rounded-lg border-white/10 focus:border-lime-500 focus:ring-lime-500/20 text-sm pr-10" required />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80">
                  {showPw ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}

            {mode === 'reset' && (
              <>
                <Input data-testid="reset-token-input" value={resetToken} onChange={e => setResetToken(e.target.value)} placeholder="Reset token"
                  className="h-11 rounded-lg border-white/10 focus:border-lime-500 focus:ring-lime-500/20 text-sm font-mono" />
                <Input data-testid="reset-new-password-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password"
                  className="h-11 rounded-lg border-white/10 focus:border-lime-500 focus:ring-lime-500/20 text-sm" required />
              </>
            )}

            {mode === 'login' && (
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="keep-signed-in-label">
                <input type="checkbox" checked={keepSignedIn} onChange={e => setKeepSignedIn(e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 text-lime-500 focus:ring-lime-500/20 cursor-pointer" data-testid="keep-signed-in-checkbox" />
                <span className="text-sm text-white/70">Keep me signed in</span>
              </label>
            )}

            {error && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-red-300 text-xs bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2" data-testid="auth-error-message">{error}</motion.p>
            )}
            {successMsg && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-lime-300 text-xs bg-lime-400/10 border border-lime-400/20 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="auth-success-message">
                <Check weight="bold" className="w-3.5 h-3.5" />{successMsg}
              </motion.p>
            )}

            <button type="submit" data-testid="auth-submit-button" disabled={loading}
              className="w-full h-11 rounded-lg bg-lime-400/100 hover:bg-lime-600 text-white font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <div className="w-4 h-4 border-2 border-lime-400/40 border-t-black rounded-full animate-spin" /> : (
                <>{mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : 'Reset password'}<ArrowRight weight="bold" className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <div className="mt-5 text-center space-y-2">
            {mode === 'login' && (
              <>
                <button onClick={() => { setMode('forgot'); setError(''); setSuccessMsg(''); }} className="text-sm text-lime-400 hover:text-lime-300 font-medium" data-testid="forgot-password-link">
                  Forgot password?
                </button>
                <p className="text-sm text-white/50">
                  Don't have an account?{' '}
                  <button onClick={() => { setMode('register'); setError(''); }} className="text-lime-400 hover:text-lime-300 font-medium" data-testid="register-tab">Sign up</button>
                </p>
              </>
            )}
            {mode === 'register' && (
              <p className="text-sm text-white/50">
                Already have an account?{' '}
                <button onClick={() => { setMode('login'); setError(''); }} className="text-lime-400 hover:text-lime-300 font-medium" data-testid="login-tab">Sign in</button>
              </p>
            )}
            {(mode === 'forgot' || mode === 'reset') && (
              <button onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }} className="text-sm text-white/50 hover:text-white/80 flex items-center justify-center gap-1" data-testid="back-to-login">
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </button>
            )}
          </div>
        </motion.div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center bg-white/5 border-l border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <motion.div className="relative z-10 text-center px-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
          <div className="w-16 h-16 rounded-2xl bg-lime-400/100 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-lime-500/20">
            <Sparkle weight="fill" className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-white mb-3" style={{ fontFamily: 'Clash Display, sans-serif' }}>Your AI assistant</h2>
          <p className="text-white/50 text-sm leading-relaxed max-w-xs mx-auto">Code generation, image creation, math solving, file analysis, and autonomous agents — all in one place.</p>
        </motion.div>
      </div>
    </div>
  );
}
